import React, { useState } from 'react';
import {
  MainWrapper,
  StyledGrid,
  StyledGridSection,
  StyledTC,
  StyledButtonWrapper,
  RadioCard,
  StyledRadio,
  Wrapper
} from './OfferSelector.styled';
import Pill from '@/components/Pill/Pill';
import { COLOR, PADDING } from '@/utils/constants';
import Button from '@latitude/button-rebrand';
import ReactHtmlParser from 'react-html-parser';
import Typography from '@latitude/typography';
import { REBRAND_COLOR } from '@latitude/core/utils/constants';

const COMPONENT_ID = 'offer-selector';

const renderOfferContent = content =>
  typeof content === 'string' ? ReactHtmlParser(content) : content;

/**
 * A component that displays selectable offers with radio buttons and an apply button.
 *
 * @param {Object} props
 * @param {Array<{id: string, title: string, content: string, productId: string}>} props.offers - Array of offer objects to display.
 *   Each offer should have: id (unique identifier), title (offer heading), content (offer description),
 *   and productId (for tracking and application URL).
 * @param {string} [props.pillText='Limited time offer'] - Text to display in the pill at the top of the component.
 * @param {string} [props.heading] - Optional heading text to display below the pill.
 * @param {string} [props.tcText] - Optional terms and conditions text to display at the bottom.
 * @param {string} [props.trackingId] - Optional tracking ID prefix for analytics.
 * @returns {JSX.Element} The rendered OfferSelector component.
 */
const OfferSelector = ({
  offers,
  pillText,
  heading,
  tcText,
  trackingId,
  hidden,
  ...rest
}) => {
  const [selected, setSelected] = useState(offers[0]);

  const componentIdentifier = trackingId
    ? `${trackingId}-${COMPONENT_ID}`
    : `${COMPONENT_ID}`;

  return (
    <Wrapper className={`${componentIdentifier}-main-wrapper`} {...rest}>
    <MainWrapper>
      {pillText && <Pill
        pillColor={COLOR.BLUE_LIGHT}
        pillText={pillText}
        pillTextColor={COLOR.BRAND_PRIMARY_90}
        className={`${componentIdentifier}-pill`}
      />}
      {heading && (
        <Typography.SH3>
          {heading}
        </Typography.SH3>
      )}
      <StyledGrid length={offers && offers.length ? offers.length : 1}>
        {offers &&
          offers.map((offer, index) => (
            <RadioCard
              key={`${componentIdentifier}-${index}`}
              selected={selected.id === offer.id}
              onClick={() => setSelected(offer)}
            >
              <StyledRadio
                checked={selected.id === offer.id}
                type="radio"
                name={selected.id}
                value={selected.id}
                onChange={() => {}}
              />
              <StyledGridSection>
                <Typography.Body small color={REBRAND_COLOR.MID_BLUE}>
                  {offer.title}
                </Typography.Body>
                <Typography.Body semi-bold color={REBRAND_COLOR.INDIGO}>
                  {offer.subtitle}
                </Typography.Body>
                <Typography.Body small color={REBRAND_COLOR.INDIGO}>
                  {renderOfferContent(offer.content)}
                </Typography.Body>
              </StyledGridSection>
            </RadioCard>
          ))}
      </StyledGrid>
      <StyledButtonWrapper>
         <Button
          variant="primary"
          trackId={`${componentIdentifier}-${selected?.ctaText || 'apply-now'}`}
          trackProductId={selected.productId}
          trackEventData={{
            location: selected.subtitle || 'offer-selector',
          }}
          target="_blank"
          href={selected?.productId ? `${selected.ctaURL}&promo_code=${selected.productId}`: selected.ctaURL}
          ctaText={ selected?.ctaText || 'Apply Now' } 
          />
      </StyledButtonWrapper>
      {tcText && <StyledTC>{tcText}</StyledTC>}
    </MainWrapper>
    </Wrapper>
  );
};

export default OfferSelector;
